import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Center,
  Loader,
  ScrollArea,
  Box,
  Anchor,
} from "@mantine/core";
import {
  IconZoomIn,
  IconZoomOut,
  IconZoomReset,
  IconExternalLink,
  IconArrowsMaximize,
  IconPhotoOff,
  IconRotateClockwise,
  IconRotateClockwise2,
} from "@tabler/icons-react";

/**
 * Renders the GR images for one AP invoice with a vertical thumbnail strip
 * on the left and a large pan-/zoom-able viewport on the right.
 *
 * The viewport uses pointer events for drag-to-pan and the wheel event for
 * zoom centred on the cursor. State (scale + translate) is reset whenever
 * the user picks a different image so the new image starts at fit.
 */
export function ImagesPane({ images, loading }) {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    setActiveIdx(0);
  }, [images?.length]);

  if (loading) {
    return (
      <Paper withBorder p="md" style={{ height: "100%" }}>
        <Center style={{ height: "100%" }}>
          <Stack align="center" gap="xs">
            <Loader />
            <Text size="xs" c="dimmed">
              Loading images…
            </Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  if (!images || images.length === 0) {
    return (
      <Paper withBorder p="md" style={{ height: "100%" }}>
        <Center style={{ height: "100%" }}>
          <Stack align="center" gap={4}>
            <IconPhotoOff size={32} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed">
              No GR images available
            </Text>
            <Text size="xs" c="dimmed">
              They will appear once the SAP snapshot is denormalised.
            </Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  const activeUrl = images[activeIdx];

  return (
    <Paper
      withBorder
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Group
        justify="space-between"
        p="xs"
        style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}
      >
        <Text size="xs" c="dimmed">
          Image {activeIdx + 1} of {images.length}
        </Text>
        <Anchor
          href={activeUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="xs"
          underline="hover"
        >
          <Group gap={4}>
            <IconExternalLink size={12} />
            <span>Open original</span>
          </Group>
        </Anchor>
      </Group>

      <Group
        gap={0}
        wrap="nowrap"
        align="stretch"
        style={{ flex: 1, minHeight: 0 }}
      >
        {images.length > 1 && (
          <ScrollArea
            style={{
              width: 84,
              borderRight: "1px solid var(--mantine-color-default-border)",
              background: "var(--mantine-color-gray-0)",
            }}
            type="auto"
          >
            <Stack gap={6} p={6}>
              {images.map((url, i) => (
                <Box
                  key={url}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveIdx(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setActiveIdx(i);
                  }}
                  style={{
                    border:
                      activeIdx === i
                        ? "2px solid var(--mantine-color-violet-6)"
                        : "2px solid transparent",
                    borderRadius: 6,
                    overflow: "hidden",
                    cursor: "pointer",
                    background: "white",
                    position: "relative",
                  }}
                >
                  <img
                    src={url}
                    alt={`GR ${i + 1}`}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: 64,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Text
                    size="xs"
                    fw={600}
                    style={{
                      position: "absolute",
                      bottom: 2,
                      right: 4,
                      color: "white",
                      textShadow: "0 0 4px rgba(0,0,0,0.8)",
                    }}
                  >
                    {i + 1}
                  </Text>
                </Box>
              ))}
            </Stack>
          </ScrollArea>
        )}

        <Box style={{ flex: 1, position: "relative", minWidth: 0 }}>
          <ZoomablePanImage src={activeUrl} />
        </Box>
      </Group>
    </Paper>
  );
}

const MIN_SCALE = 0.2;
const MAX_SCALE = 12;

function ZoomablePanImage({ src }) {
  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const dragStateRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [dragging, setDragging] = useState(false);

  const reset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setRotation(0);
  }, []);

  useEffect(() => {
    // Reset zoom/pan/rotation whenever the image source changes.
    reset();
  }, [src, reset]);

  const rotateCW = () => setRotation((r) => (r + 90) % 360);
  const rotateCCW = () => setRotation((r) => (r + 270) % 360);

  const clamp = (s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));

  // Native wheel listener with passive:false so we can preventDefault
  // (React's onWheel runs as passive, which would otherwise scroll the page).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    // Sensitivity: zoom factor = exp(-deltaY * SENSITIVITY).
    // Smaller value = slower zoom. Tuned so a typical mouse wheel tick
    // (deltaY ≈ 100) moves ~6%, and a trackpad pinch / fine scroll
    // (deltaY ≈ 4-10) barely nudges the scale.
    const SENSITIVITY = 0.0006;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      // deltaMode 1 = lines, 2 = pages — normalise to pixels-ish.
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;
      else if (e.deltaMode === 2) dy *= 100;
      // Cap one event's effect so an aggressive flick can't snap to min/max.
      const cappedDy = Math.max(-200, Math.min(200, dy));
      const factor = Math.exp(-cappedDy * SENSITIVITY);
      setScale((prev) => {
        const next = clamp(prev * factor);
        const ratio = next / prev;
        setTranslate((t) => ({
          x: cx - (cx - t.x) * ratio,
          y: cy - (cy - t.y) * ratio,
        }));
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    // Ignore pointerdown on interactive children (toolbar buttons, anchors,
    // the open-original link, etc). Without this guard, `setPointerCapture`
    // below redirects subsequent pointer events to the viewport and the
    // browser swallows the synthetic `click` on the original target — the
    // result was that toolbar buttons appeared to need a double-click and
    // rotate/zoom/reset didn't seem to fire at all.
    if (
      e.target &&
      typeof e.target.closest === "function" &&
      e.target.closest("button, a, [data-no-drag]")
    ) {
      return;
    }
    viewportRef.current?.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      x: e.clientX,
      y: e.clientY,
      tx: translate.x,
      ty: translate.y,
    };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    const st = dragStateRef.current;
    if (!st) return;
    setTranslate({
      x: st.tx + (e.clientX - st.x),
      y: st.ty + (e.clientY - st.y),
    });
  };

  const onPointerUp = (e) => {
    dragStateRef.current = null;
    setDragging(false);
    try {
      viewportRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onDoubleClick = (e) => {
    if (
      e?.target &&
      typeof e.target.closest === "function" &&
      e.target.closest("button, a, [data-no-drag]")
    ) {
      return;
    }
    if (scale === 1) {
      setScale(2);
    } else {
      reset();
    }
  };

  const zoomIn = () => setScale((s) => clamp(s * 1.4));
  const zoomOut = () => setScale((s) => clamp(s / 1.4));

  const transform = useMemo(
    () =>
      `translate(${translate.x}px, ${translate.y}px) scale(${scale}) rotate(${rotation}deg)`,
    [translate.x, translate.y, scale, rotation]
  );

  return (
    <Box
      ref={viewportRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        background:
          "repeating-conic-gradient(#f1f3f5 0% 25%, #ffffff 0% 50%) 50% / 24px 24px",
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt="GR"
        draggable={false}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          maxWidth: "100%",
          maxHeight: "100%",
          transform: `translate(-50%, -50%) ${transform}`,
          transformOrigin: "center center",
          transition: dragging ? "none" : "transform 90ms ease-out",
          willChange: "transform",
          pointerEvents: "none",
        }}
      />

      <Group
        gap={4}
        p={6}
        style={{
          position: "absolute",
          right: 8,
          bottom: 8,
          background: "rgba(255,255,255,0.92)",
          borderRadius: 6,
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        }}
      >
        <Tooltip label="Zoom out">
          <ActionIcon size="sm" variant="subtle" onClick={zoomOut}>
            <IconZoomOut size={14} />
          </ActionIcon>
        </Tooltip>
        <Text size="xs" w={42} ta="center">
          {Math.round(scale * 100)}%
        </Text>
        <Tooltip label="Zoom in">
          <ActionIcon size="sm" variant="subtle" onClick={zoomIn}>
            <IconZoomIn size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Rotate left 90°">
          <ActionIcon size="sm" variant="subtle" onClick={rotateCCW}>
            <IconRotateClockwise2 size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Rotate right 90°">
          <ActionIcon size="sm" variant="subtle" onClick={rotateCW}>
            <IconRotateClockwise size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Reset (fit & 0°)">
          <ActionIcon size="sm" variant="subtle" onClick={reset}>
            <IconZoomReset size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="100%">
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={() => {
              setScale(1);
              setTranslate({ x: 0, y: 0 });
            }}
          >
            <IconArrowsMaximize size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}
